/** Positions / orders / fills for the active account, kept live via the user:<accountId> channel. */
import { useEffect, useMemo } from 'react';
import { accountMarginLevel, closePrice, D, pnl, usdPerPoint, type AccountSummary, type Fill, type Order, type Position, type UserEvent } from '@platform/shared';
import { api } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { ws } from '../lib/ws';
import { marketStore } from './market';
import { updateAccount, useActiveAccount } from './session';

interface TradingState {
  accountId: string | null;
  loading: boolean;
  positions: Position[];
  closed: Position[];
  orders: Order[];
  fills: Fill[];
}

export const tradingStore = createStore<TradingState>({ accountId: null, loading: false, positions: [], closed: [], orders: [], fills: [] });

export async function reloadTrading(accountId: string) {
  tradingStore.set({ loading: true });
  try {
    const [positions, closed, orders, fills, summary] = await Promise.all([
      api.get<Position[]>(`/api/positions?accountId=${accountId}&status=open`),
      api.get<Position[]>(`/api/positions?accountId=${accountId}&status=closed`),
      api.get<Order[]>(`/api/orders?accountId=${accountId}&status=pending`),
      api.get<Fill[]>(`/api/history/fills?accountId=${accountId}&limit=100`),
      api.get<AccountSummary>(`/api/accounts/${accountId}/summary`),
    ]);
    if (tradingStore.get().accountId !== accountId) return;
    tradingStore.set({ positions, closed, orders, fills, loading: false });
    updateAccount(summary);
  } catch {
    tradingStore.set({ loading: false });
  }
}

function onUserEvent(accountId: string, ev: UserEvent) {
  if (tradingStore.get().accountId !== accountId) return;
  if (ev.type === 'balance') {
    updateAccount(ev.account);
  } else if (ev.type === 'position') {
    const p = ev.position;
    tradingStore.set((s) => {
      const rest = s.positions.filter((x) => x.id !== p.id);
      if (p.status === 'open') return { positions: [p, ...rest] };
      return { positions: rest, closed: [p, ...s.closed.filter((x) => x.id !== p.id)] };
    });
    if (ev.event === 'closed' || ev.event === 'liquidated' || ev.event === 'opened') void refreshFills(accountId);
  } else if (ev.type === 'order') {
    const o = ev.order;
    tradingStore.set((s) => {
      const rest = s.orders.filter((x) => x.id !== o.id);
      return { orders: o.status === 'pending' ? [o, ...rest] : rest };
    });
  }
}

async function refreshFills(accountId: string) {
  try {
    const fills = await api.get<Fill[]>(`/api/history/fills?accountId=${accountId}&limit=100`);
    if (tradingStore.get().accountId === accountId) tradingStore.set({ fills });
  } catch {
    /* ignore */
  }
}

/** Mount once: follows the active account, loads its data and keeps it live. */
export function useTradingSync() {
  const acc = useActiveAccount();
  const id = acc?.id ?? null;
  useEffect(() => {
    tradingStore.set({ accountId: id, positions: [], closed: [], orders: [], fills: [] });
    if (!id) return;
    void reloadTrading(id);
    const unsub = ws.subscribe(`user:${id}`, (ev: UserEvent) => onUserEvent(id, ev));
    // Periodic resync covers anything missed during a reconnect.
    const t = window.setInterval(() => void reloadTrading(id), 60_000);
    return () => {
      unsub();
      window.clearInterval(t);
    };
  }, [id]);
}

export const usePositions = () => useStore(tradingStore, (s) => s.positions);
export const useClosedPositions = () => useStore(tradingStore, (s) => s.closed);
export const useOrders = () => useStore(tradingStore, (s) => s.orders);
export const useFills = () => useStore(tradingStore, (s) => s.fills);

/** Active account with unrealized PnL / equity re-marked live from the ticker stream. */
export function useLiveAccount(): AccountSummary | null {
  const acc = useActiveAccount();
  const positions = usePositions();
  const tickers = useStore(marketStore, (s) => s.tickers);
  const bySymbol = useStore(marketStore, (s) => s.bySymbol);
  return useMemo(() => {
    if (!acc) return null;
    if (!positions.length) return acc;
    let u = D.ZERO;
    for (const p of positions) {
      const t = tickers[p.symbol];
      if (!t) continue;
      const exit = closePrice(p.side, { bid: D.from(t.bid), ask: D.from(t.ask) });
      u = u.add(pnl(p.side, p.entryPrice, exit, p.qty, usdPerPoint(bySymbol[p.symbol]?.contractSize ?? '1', t.fx))).add(p.swap);
    }
    return { ...acc, unrealizedPnl: u.toString(), equity: D.from(acc.balance).add(acc.credit).add(u).toString() };
  }, [acc, positions, tickers, bySymbol]);
}

/** Account margin level (equity ÷ used margin, 1 = 100 %), marked live; null with no margin used. */
export function useAccountMarginLevel(): D | null {
  const acc = useLiveAccount();
  return acc ? accountMarginLevel(acc.equity, acc.marginUsed) : null;
}
